import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { Job, JobsResponse, TrackingPixels } from 'types';
import { ExperiencePayload, SummaryPayload } from 'types/chatgpt';

import type { Payload as SponsoredJobPayload } from 'hooks/useSponsoredJobs';
import { HttpRoutes } from 'utils/constant';

const removeApiPath = (path: string) =>
  path.startsWith('/api/') ? path.replace('/api', '') : path;

export const api = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api',
  }),
  tagTypes: ['API'],
  endpoints: (build) => ({
    getSponsoredJob: build.query<Job, SponsoredJobPayload>({
      query: (body) => {
        return {
          url: removeApiPath(HttpRoutes.fetchSponsoredJobs),
          method: 'POST',
          body,
        };
      },
      transformResponse: (response: { jobs: Job[] }) => response?.jobs?.[0],
      providesTags: [{ type: 'API', id: 'Sponsored Job - JLS API' }],
    }),
    getSearchApiSponsoredJob: build.query({
      query: (body) => {
        const reqBody = { ...body };
        delete reqBody.fullResponse;
        return {
          url: removeApiPath(HttpRoutes.fetchSearchApiSponsoredJobs),
          method: 'POST',
          body: reqBody,
        };
      },
      transformResponse: (response: JobsResponse, _, { fullResponse }) => {
        if (fullResponse) return response;
        return response?.jobs?.[0];
      },
      providesTags: [{ type: 'API', id: 'Sponsored Job - Search API' }],
    }),
    getTrackingPixels: build.query<TrackingPixels, void>({
      query: () => ({ url: removeApiPath(HttpRoutes.getTrackingPixels) }),
      transformResponse: (response: TrackingPixels) => response ?? {},
      providesTags: [{ type: 'API', id: 'Tracking Pixels' }],
    }),
    generateWorkHistoryAI: build.query<string, ExperiencePayload>({
      query: (body) => ({
        url: removeApiPath(HttpRoutes.generateWorkExperience),
        method: 'POST',
        body,
      }),
      providesTags: [{ type: 'API', id: 'AI - Work History' }],
    }),
    generateSummaryAI: build.query<string, SummaryPayload>({
      query: (body) => ({
        url: removeApiPath(HttpRoutes.generateSummary),
        method: 'POST',
        body,
      }),
      providesTags: [{ type: 'API', id: 'AI - Summary' }],
    }),
  }),
});

export const {
  useLazyGetSponsoredJobQuery,
  useLazyGetTrackingPixelsQuery,
  useLazyGetSearchApiSponsoredJobQuery,
  useGetTrackingPixelsQuery,
  useLazyGenerateWorkHistoryAIQuery,
  useLazyGenerateSummaryAIQuery,
} = api;
